/**
 * <Calendario> — o seletor de data+hora canônico do sistema (FILA-D, item 1).
 *
 * Nasceu do mockup aprovado pelo Matheus em 04/08/2026 (variação A: mês à
 * esquerda, horários ao lado, hora exata embaixo) e substitui os inputs
 * `datetime-local`/`date` soltos nos ~12 pontos que agendam — como o Tooltip
 * e o Avatar, ninguém volta a implementar calendário na mão.
 *
 * O que ele resolve, por decisão de produto:
 *  - HORA JUNTO DO DIA: um gesto só, nada de segundo campo em outra linha;
 *  - FIM DE SEMANA hachurado e FERIADO nomeado — direto do motor de
 *    Configurações › Sistema (useHorarioExpediente/useHorarioFeriados),
 *    nunca uma segunda lista;
 *  - HOJE tem contorno; o DIA ESCOLHIDO é vermelho cheio;
 *  - mês inteiro sem rolagem, inclusive em 375px;
 *  - passado e dia sem expediente AVISAM, não bloqueiam;
 *  - AGENDA DO DIA + CONFLITO (pedido do Matheus): o ponto de uso injeta os
 *    compromissos do responsável; horário em cima de compromisso (1h) mostra
 *    o aviso e o botão vira "Agendar mesmo assim" — habilitado. A pessoa
 *    decide sabendo.
 *
 * O componente NÃO busca agenda sozinho: quem sabe de quem é a agenda é o
 * formulário (responsável da atividade ≠ usuário logado). `compromissos` é
 * opcional; sem ele, o fluxo de conflito simplesmente não existe.
 *
 * `soData` cobre os usos legítimos de só-data (vigência de contrato, feriado
 * da configuração) com o MESMO componente — variante, não segundo motor.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useHorarioExpediente, useHorarioFeriados } from '@/lib/horario-store';
import { CAMADA } from '@/lib/camadas';
import { cn } from '@/lib/cn';
import { Button } from './Button';
import { HelpTip } from './HelpTip';

const NOMES_MES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];
const DIAS_SEMANA = ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'];
const DURACAO_COMPROMISSO_MIN = 60; // padrão do evento de 1h do Google Agenda

export interface CompromissoDia {
  /** 'YYYY-MM-DDTHH:mm' — de onde vier (atividades do responsável). */
  quando: string;
  titulo: string;
}

function chaveDia(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function mesmoDia(a: Date | null, b: Date | null): boolean {
  return Boolean(a && b && chaveDia(a) === chaveDia(b));
}
function minutosDe(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

export function Calendario(props: {
  /** 'YYYY-MM-DDTHH:mm' (ou 'YYYY-MM-DD' com soData) — '' = sem escolha. */
  valor: string;
  onChange: (v: string) => void;
  /** Rótulo do gatilho quando vazio. */
  placeholder?: string;
  /** Variante só-data (vigências, feriados) — sem coluna de horários. */
  soData?: boolean;
  /** Agenda do dia (conflitos) — injetada pelo formulário dono do contexto. */
  compromissos?: CompromissoDia[];
  desabilitado?: boolean;
  testId?: string;
}) {
  const hoje = useMemo(() => new Date(), []);
  const [aberto, setAberto] = useState(false);

  const valorDia = props.valor ? props.valor.slice(0, 10) : null;
  const valorHora = props.valor && props.valor.includes('T') ? props.valor.slice(11, 16) : null;
  const [dia, setDia] = useState<Date | null>(valorDia ? new Date(`${valorDia}T00:00`) : null);
  const [hora, setHora] = useState<string | null>(valorHora);
  const [ano, setAno] = useState(dia?.getFullYear() ?? hoje.getFullYear());
  const [mes, setMes] = useState(dia?.getMonth() ?? hoje.getMonth());

  const { data: expediente } = useHorarioExpediente();
  const { data: feriados = [] } = useHorarioFeriados(ano);
  const raizRef = useRef<HTMLDivElement>(null);
  const painelRef = useRef<HTMLDivElement>(null);

  /**
   * PORTAL + FLIP/SHIFT (URGENTE 05/08 — o Matheus não conseguia escolher a
   * hora no modal "Editar proposta"): o painel era `position:absolute` DENTRO
   * da árvore do gatilho e qualquer modal/card com overflow o CLIPAVA — a
   * mesma doença que o Tooltip já teve e curou. Mesma cura: renderizado em
   * portal no body, `position:fixed` calculado do rect do gatilho, virando
   * pra cima quando não cabe embaixo e deslizando na horizontal pra nunca
   * sair da viewport. Regra do projeto desde julho; o Calendario nasceu sem.
   */
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const FOLGA = 8;
  const reposicionar = useCallback(() => {
    const gatilho = raizRef.current;
    const painel = painelRef.current;
    if (!gatilho || !painel) return;
    const r = gatilho.getBoundingClientRect();
    const p = painel.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // FLIP vertical: preferência embaixo; sem espaço embaixo E com espaço em
    // cima, abre pra cima. Sem espaço nos DOIS (notebook 1366×768 com o
    // gatilho no meio de um modal — o caso do incidente de altura), encosta no
    // rodapé da viewport: o painel é `fixed`, rolagem de página NENHUMA o
    // alcança, então "ficar embaixo clampado" deixava as horas e o Confirmar
    // fora da tela. Painel mais alto que a viewport rola por dentro (max-h).
    const cabeEmbaixo = r.bottom + 4 + p.height <= vh - FOLGA;
    const cabeEmCima = r.top - 4 - p.height >= FOLGA;
    const top = cabeEmbaixo
      ? r.bottom + 4
      : cabeEmCima
        ? r.top - 4 - p.height
        : Math.max(FOLGA, vh - FOLGA - p.height);
    // SHIFT horizontal: alinha à esquerda do gatilho e clampa na viewport.
    const left = Math.max(FOLGA, Math.min(r.left, vw - p.width - FOLGA));
    setPos({ top: Math.max(FOLGA, top), left });
  }, []);

  useLayoutEffect(() => {
    if (!aberto) {
      setPos(null);
      return;
    }
    reposicionar();
    // mes/ano/dia/hora mudam a ALTURA do painel (avisos entram e saem) — o
    // flip é recalculado com o tamanho real de cada estado.
  }, [aberto, reposicionar, mes, ano, dia, hora]);

  // Esc fecha; clique fora fecha — mesmo contrato dos popovers canônicos.
  // Com o portal, "dentro" são DUAS árvores: o gatilho e o painel.
  useEffect(() => {
    if (!aberto) return;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A-7 (AUDITORIA-COMERCIAL): Esc no painel aberto fecha SÓ o painel —
      // captura + stopImmediatePropagation, senão o Dialog/tela cheia de fora
      // fecha junto e leva o formulário meio preenchido.
      e.stopImmediatePropagation();
      e.preventDefault();
      setAberto(false);
    };
    const aoClicar = (e: MouseEvent) => {
      const alvo = e.target as Node;
      const dentro = raizRef.current?.contains(alvo) || painelRef.current?.contains(alvo);
      if (!dentro) setAberto(false);
    };
    window.addEventListener('keydown', aoTeclar, true);
    window.addEventListener('mousedown', aoClicar);
    window.addEventListener('scroll', reposicionar, true);
    window.addEventListener('resize', reposicionar);
    // O evento `resize` não cobre tudo (emulação de viewport, zoom, teclado
    // do celular): o ResizeObserver no documento e no próprio painel pega o
    // que escapar.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => reposicionar()) : null;
    ro?.observe(document.documentElement);
    if (painelRef.current) ro?.observe(painelRef.current);
    return () => {
      window.removeEventListener('keydown', aoTeclar, true);
      window.removeEventListener('mousedown', aoClicar);
      window.removeEventListener('scroll', reposicionar, true);
      window.removeEventListener('resize', reposicionar);
      ro?.disconnect();
    };
  }, [aberto, reposicionar]);

  const feriadoDe = (d: Date) => feriados.find((f) => f.ativo && f.data === chaveDia(d)) ?? null;
  // "Sem expediente" vem do MOTOR (dia da semana com aberto=false na configuração),
  // não de sábado/domingo cravado — se a empresa abrir no sábado, a tela obedece.
  const expedienteDe = (d: Date) => expediente?.find((e) => e.diaSemana === d.getDay()) ?? null;
  const ehFds = (d: Date) => {
    const e = expedienteDe(d);
    if (e) return !e.aberto;
    const dow = (d.getDay() + 6) % 7;
    return dow >= 5; // fallback sem config carregada
  };

  const celulas = useMemo(() => {
    const primeiro = new Date(ano, mes, 1);
    const inicio = (primeiro.getDay() + 6) % 7; // semana começa na segunda
    const nDias = new Date(ano, mes + 1, 0).getDate();
    const anteriores = new Date(ano, mes, 0).getDate();
    const lista: Array<{ d: Date; fora: boolean }> = [];
    for (let i = inicio; i > 0; i--) lista.push({ d: new Date(ano, mes - 1, anteriores - i + 1), fora: true });
    for (let n = 1; n <= nDias; n++) lista.push({ d: new Date(ano, mes, n), fora: false });
    while (lista.length % 7 !== 0) {
      lista.push({ d: new Date(ano, mes + 1, lista.length - nDias - inicio + 1), fora: true });
    }
    return lista;
  }, [ano, mes]);

  const compromissosDoDia = useMemo(() => {
    if (!dia || !props.compromissos) return [];
    const k = chaveDia(dia);
    return props.compromissos
      .filter((c) => c.quando.slice(0, 10) === k && c.quando.includes('T'))
      .map((c) => ({ ...c, hora: c.quando.slice(11, 16) }))
      .sort((a, b) => (a.hora < b.hora ? -1 : 1));
  }, [dia, props.compromissos]);

  function conflitoCom(hhmm: string | null) {
    if (!hhmm) return null;
    const t = minutosDe(hhmm);
    return (
      compromissosDoDia.find((c) => t >= minutosDe(c.hora) && t < minutosDe(c.hora) + DURACAO_COMPROMISSO_MIN) ?? null
    );
  }

  const conflito = conflitoCom(hora);
  const passado = Boolean(dia && chaveDia(dia) < chaveDia(hoje));
  const feriadoSel = dia ? feriadoDe(dia) : null;
  const semExpediente = Boolean(dia && (ehFds(dia) || feriadoSel));
  const completo = Boolean(dia && (props.soData || hora));

  function confirmar() {
    if (!dia || (!props.soData && !hora)) return;
    props.onChange(props.soData ? chaveDia(dia) : `${chaveDia(dia)}T${hora}`);
    setAberto(false);
  }

  function limpar() {
    setDia(null);
    setHora(null);
    props.onChange('');
    setAberto(false);
  }

  const horarios = useMemo(() => {
    const lista: Array<{ rotulo: string; foraExpediente: boolean }> = [];
    // Janela ampla 06-21h; o expediente configurado destaca o miolo.
    const ini = 6;
    const fim = 21;
    const expDia = dia ? (expediente?.find((e) => e.diaSemana === dia.getDay()) ?? null) : null;
    const expIni = expDia?.aberto && expDia.inicio ? Number(expDia.inicio.slice(0, 2)) : 8;
    const expFim = expDia?.aberto && expDia.fim ? Number(expDia.fim.slice(0, 2)) : 18;
    for (let h = ini; h <= fim; h++) {
      for (const m of [0, 30]) {
        lista.push({
          rotulo: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
          foraExpediente: h < expIni || h >= expFim,
        });
      }
    }
    return lista;
  }, [expediente, dia]);

  const rotuloValor = props.valor
    ? props.soData
      ? props.valor.split('-').reverse().join('/')
      : `${props.valor.slice(8, 10)}/${props.valor.slice(5, 7)} às ${props.valor.slice(11, 16)}`
    : null;

  return (
    <div ref={raizRef} className="relative inline-block" data-testid={props.testId ?? 'calendario'}>
      <button
        type="button"
        disabled={props.desabilitado}
        data-testid="calendario-gatilho"
        onClick={() => setAberto((v) => !v)}
        className={cn(
          'flex items-center gap-2 rounded-lg border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface px-3 py-2 text-sm',
          rotuloValor ? 'font-semibold text-ink-1 dark:text-ink-dark-1' : 'text-ink-3 dark:text-ink-dark-3',
        )}
      >
        {rotuloValor ?? props.placeholder ?? (props.soData ? 'Escolher data' : 'Escolher data e hora')}
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M8 3v4M16 3v4M3 10h18" />
        </svg>
      </button>

      {aberto &&
        typeof document !== 'undefined' &&
        createPortal(
        <div
          ref={painelRef}
          role="dialog"
          aria-label={props.soData ? 'Escolher data' : 'Escolher data e hora'}
          data-testid="calendario-painel"
          style={{
            position: 'fixed',
            top: pos?.top ?? -9999,
            left: pos?.left ?? -9999,
            visibility: pos ? 'visible' : 'hidden',
            zIndex: CAMADA.FLUTUANTE,
          }}
          className="w-[min(92vw,360px)] max-h-[calc(100vh-16px)] overflow-y-auto rounded-xl border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface shadow-xl p-3"
        >
          <div className="flex items-center gap-1 mb-2">
            <Button variant="secondary" size="sm" aria-label="Mês anterior" onClick={() => (mes === 0 ? (setMes(11), setAno(ano - 1)) : setMes(mes - 1))}>
              ‹
            </Button>
            <span className="flex-1 text-center font-display text-sm" data-testid="calendario-mes">
              {NOMES_MES[mes]} {ano}
            </span>
            <Button variant="secondary" size="sm" aria-label="Mês seguinte" onClick={() => (mes === 11 ? (setMes(0), setAno(ano + 1)) : setMes(mes + 1))}>
              ›
            </Button>
          </div>

          <div className={cn('flex gap-2.5', props.soData && 'block')}>
            <div className="flex-1 min-w-0">
              <div className="grid grid-cols-7 gap-0.5 text-center">
                {DIAS_SEMANA.map((d) => (
                  <span key={d} className="text-[9px] font-bold uppercase text-ink-3 dark:text-ink-dark-3 py-0.5">
                    {d}
                  </span>
                ))}
                {celulas.map(({ d, fora }) => {
                  const feriado = feriadoDe(d);
                  return (
                    <button
                      key={chaveDia(d) + (fora ? '-f' : '')}
                      type="button"
                      title={feriado?.nome}
                      data-testid={`calendario-dia-${chaveDia(d)}`}
                      onClick={() => {
                        setDia(d);
                        if (fora) {
                          setAno(d.getFullYear());
                          setMes(d.getMonth());
                        }
                      }}
                      className={cn(
                        'relative h-8 rounded-md text-[13px]',
                        // URGENTE 05/08 (2b): fim de semana ATENUADO, nunca
                        // apagado — cinza-sobre-cinza (ink-3 no bg line-soft)
                        // sumia com o número do dia. O "8" do sábado continua
                        // legível; a hachura de fundo já diz "sem expediente".
                        fora && 'text-ink-3/50 dark:text-ink-dark-3/50',
                        ehFds(d) && 'bg-line-soft/60 dark:bg-line-soft-dark/60',
                        ehFds(d) && !fora && 'text-ink-2 dark:text-ink-dark-2',
                        feriado && 'bg-red-soft dark:bg-red-soft-dark text-red-deep dark:text-red-glow',
                        mesmoDia(d, hoje) && 'outline outline-2 -outline-offset-2 outline-ink-2 dark:outline-ink-dark-2 font-bold',
                        mesmoDia(d, dia) && 'bg-red text-white font-bold outline-none',
                      )}
                    >
                      {d.getDate()}
                      {feriado && !mesmoDia(d, dia) && (
                        <span aria-hidden className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-red" />
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1.5 text-[9px] text-ink-3 dark:text-ink-dark-3">
                contorno = hoje · cheio = escolhido · hachura = fim de semana · rosado = feriado
              </p>
            </div>

            {!props.soData && (
              <div className="w-[104px] shrink-0 border-l border-dashed border-line dark:border-line-dark pl-2.5 flex flex-col">
                <p className="text-[9px] font-bold uppercase tracking-wide text-ink-3 dark:text-ink-dark-3 mb-1">Horário</p>
                <div className="overflow-y-auto max-h-52 flex flex-col gap-0.5" data-testid="calendario-horas">
                  {horarios.map((h) => {
                    const ocupado = Boolean(conflitoCom(h.rotulo));
                    return (
                      <button
                        key={h.rotulo}
                        type="button"
                        data-testid={`calendario-hora-${h.rotulo}`}
                        title={ocupado ? 'Já tem compromisso neste horário' : undefined}
                        onClick={() => setHora(h.rotulo)}
                        className={cn(
                          'rounded-md border border-line dark:border-line-dark py-0.5 text-xs',
                          h.foraExpediente && 'opacity-40',
                          ocupado && hora !== h.rotulo && 'border-red shadow-[inset_2px_0_0_theme(colors.red)]',
                          hora === h.rotulo && 'bg-red border-red text-white font-bold',
                        )}
                      >
                        {h.rotulo}
                      </button>
                    );
                  })}
                </div>
                <label className="mt-1.5 text-[9px] text-ink-3 dark:text-ink-dark-3">
                  hora exata
                  <input
                    data-testid="calendario-hora-exata"
                    value={hora ?? ''}
                    placeholder="14:37"
                    onChange={(e) => /^\d{2}:\d{2}$/.test(e.target.value) && setHora(e.target.value)}
                    className="mt-0.5 w-full rounded-md border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface px-1.5 py-1 text-xs text-ink-1 dark:text-ink-dark-1"
                  />
                </label>
              </div>
            )}
          </div>

          {compromissosDoDia.length > 0 && (
            <div data-testid="calendario-agenda-dia" className="mt-2 rounded-lg border border-dashed border-line dark:border-line-dark px-2.5 py-1.5 text-xs">
              <p className="text-[9px] font-bold uppercase tracking-wide text-ink-3 dark:text-ink-dark-3">
                Agenda do responsável no dia
              </p>
              {compromissosDoDia.map((c) => (
                <p key={c.quando + c.titulo} className="text-ink-2 dark:text-ink-dark-2">
                  · <b>{c.hora}</b> {c.titulo}
                </p>
              ))}
            </div>
          )}

          {conflito && (
            <div data-testid="calendario-aviso-conflito" className="mt-2 rounded-lg bg-red-soft dark:bg-red-soft-dark text-red-deep dark:text-red-glow px-2.5 py-1.5 text-xs font-semibold">
              Conflito: às {conflito.hora} já existe “{conflito.titulo}”. Dá pra agendar assim mesmo; o aviso é pra decidir sabendo.
            </div>
          )}
          {passado && (
            <div data-testid="calendario-aviso-passado" className="mt-2 rounded-lg bg-warning/10 text-warning px-2.5 py-1.5 text-xs font-semibold">
              Essa data já passou. Dá pra agendar mesmo assim; confere se era isso.
            </div>
          )}
          {semExpediente && (
            <div data-testid="calendario-aviso-expediente" className="mt-2 rounded-lg bg-warning/10 text-warning px-2.5 py-1.5 text-xs font-semibold">
              Dia sem expediente ({feriadoSel ? feriadoSel.nome : 'fim de semana'}). O lembrete vai tocar num dia que ninguém trabalha.
            </div>
          )}

          <div className="mt-2.5 pt-2 border-t border-line-soft dark:border-line-soft-dark flex items-center gap-2">
            <span className="font-display text-sm" data-testid="calendario-resumo">
              {dia ? (
                <>
                  {DIAS_SEMANA[(dia.getDay() + 6) % 7]} {String(dia.getDate()).padStart(2, '0')}/{String(dia.getMonth() + 1).padStart(2, '0')}
                  {!props.soData && (hora ? ` · ${hora}` : '')}
                </>
              ) : (
                <span className="font-sans text-xs text-ink-3 dark:text-ink-dark-3">nada escolhido ainda</span>
              )}
              {!props.soData && dia && !hora && (
                <span className="font-sans text-xs text-ink-3 dark:text-ink-dark-3"> · falta a hora</span>
              )}
            </span>
            <HelpTip texto="Escolha o dia no mês e a hora ao lado (os horários de 30 em 30 são atalho; a hora exata se escreve embaixo). Fim de semana e feriado vêm da configuração de expediente do sistema." />
            {props.valor && (
              <Button variant="ghost" size="sm" onClick={limpar}>
                Limpar
              </Button>
            )}
            <Button size="sm" className="ml-auto" data-testid="calendario-confirmar" disabled={!completo} onClick={confirmar}>
              {conflito ? 'Agendar mesmo assim' : props.soData ? 'Escolher' : 'Agendar'}
            </Button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
